import { equals2, go, mapL } from "fxjs/es";
import { CommonNS } from "../../../../Common/CommonNS.js";
import { VONS } from "../../VO/VONS.js";
export class ViewBox {
  constructor({ svg_el, min_s, max_s, x, y, width, height, matrix }) {
    if (min_s <= 0) {
      throw new Error(`min_s 는 음수일 수 없습니다.`);
    }
    if (max_s <= 0) {
      throw new Error(`max_s 는 음수일 수 없습니다.`);
    }
    if (min_s > max_s) {
      throw new Error(`min_s 는 max_s 보다 클 수 없습니다.`);
    }
    this._observer_set = new Set();
    this._svg_el = svg_el;
    this._min_s = min_s;
    this._max_s = max_s;
    this._x = x ?? 0;
    this._y = y ?? 0;
    this._width = width ?? 0;
    this._height = height ?? 0;
    this._matrix = matrix ?? svg_el.createSVGMatrix();
  }
  subscribe(observer) {
    if (this._observer_set.has(observer)) {
      throw new Error(`이미 등록된 observer 입니다.`);
    }
    observer(
      this._calculateViewBoxVO({
        x: this._x,
        y: this._y,
        width: this._width,
        height: this._height,
        matrix: this._matrix,
      })
    );
    this._observer_set.add(observer);
    return () => {
      this._observer_set.delete(observer);
    };
  }
  getViewBox() {
    return this._calculateViewBoxVO({
      x: this._x,
      y: this._y,
      width: this._width,
      height: this._height,
      matrix: this._matrix,
    });
  }
  setViewBox({ x, y, width, height }) {
    for (const [length, name] of [
      [width, "width"],
      [height, "height"],
    ]) {
      if (length <= 0) {
        throw new Error(`${name} 는 0 보다 커야 합니다.`);
      }
    }
    for (const [value, name] of [
      [x, "x"],
      [y, "y"],
      [width, "width"],
      [height, "height"],
    ]) {
      if (Number.isNaN(value)) {
        throw new Error(`${name} 가 유효하지 않습니다. (NaN)`);
      }
      if (!Number.isFinite(value)) {
        throw new Error(`${name} 가 유효하지 않습니다. (Infinity)`);
      }
    }
    this._x = x;
    this._y = y;
    this._width = width;
    this._height = height;
    this._matrix = this._svg_el.createSVGMatrix();
    this._notify({
      x: this._x,
      y: this._y,
      width: this._width,
      height: this._height,
      matrix: this._matrix,
    });
  }
  calculateFitTo({
    user_coordinate_system_area: ucs_area,
    viewport_coordinate_system_area: vcs_area,
    view_port,
    vertical_align,
  }) {
    for (const [length, name] of [
      [view_port.width, "view_port.width"],
      [view_port.height, "view_port.height"],
      [ucs_area.width, "user_coordinate_system.width"],
      [ucs_area.height, "user_coordinate_system.height"],
      [vcs_area.width, "viewport_coordinate_system.width"],
      [vcs_area.height, "viewport_coordinate_system.height"],
    ]) {
      if (length <= 0) {
        throw new Error(`${name} 는 0 보다 커야 합니다.`);
      }
      if (Number.isNaN(length)) {
        throw new Error(`${name} 가 유효하지 않습니다. (NaN)`);
      }
      if (!Number.isFinite(length)) {
        throw new Error(`${name} 가 유효하지 않습니다. (Infinity)`);
      }
    }
    for (const [value, name] of [
      [ucs_area.x, "user_coordinate_system.x"],
      [ucs_area.y, "user_coordinate_system.y"],
      [vcs_area.x, "viewport_coordinate_system.x"],
      [vcs_area.y, "viewport_coordinate_system.y"],
    ]) {
      if (Number.isNaN(value)) {
        throw new Error(`${name} 가 유효하지 않습니다. (NaN)`);
      }
      if (!Number.isFinite(value)) {
        throw new Error(`${name} 가 유효하지 않습니다. (Infinity)`);
      }
    }
    const sx = ucs_area.width / vcs_area.width;
    const sy = ucs_area.height / vcs_area.height;
    const s = Math.max(sx, sy);
    const scaled_vcs_area = {
      x: vcs_area.x * s,
      y: vcs_area.y * s,
      width: vcs_area.width * s,
      height: vcs_area.height * s,
    };
    const scaled_view_port = {
      width: view_port.width * s,
      height: view_port.height * s,
    };
    const x = -(
      scaled_vcs_area.x -
      ucs_area.x +
      (scaled_vcs_area.width - ucs_area.width) / 2
    );
    const y = (() => {
      if (equals2(vertical_align)("top")) {
        return -scaled_vcs_area.y + ucs_area.y;
      }
      return -(
        scaled_vcs_area.y -
        ucs_area.y +
        (scaled_vcs_area.height - ucs_area.height) / 2
      );
    })();
    return this._calculateViewBoxVO({
      x,
      y,
      width: scaled_view_port.width,
      height: scaled_view_port.height,
      matrix: this._svg_el.createSVGMatrix(),
    });
  }
  addTransform({ matrix }) {
    const new_matrix = matrix.multiply(this._matrix);
    const { width, height } = this._calculateViewBoxVO({
      x: this._x,
      y: this._y,
      width: this._width,
      height: this._height,
      matrix: new_matrix,
    });
    const sx = this._width / width;
    const sy = this._height / height;
    if (Math.min(sx, sy) < this._min_s) {
      return;
    }
    if (Math.max(sx, sy) > this._max_s) {
      return;
    }
    this._matrix = new_matrix;
    this._notify({
      x: this._x,
      y: this._y,
      width: this._width,
      height: this._height,
      matrix: this._matrix,
    });
  }
  _notify({ x, y, width, height, matrix }) {
    const view_box_vo = this._calculateViewBoxVO({
      x,
      y,
      width,
      height,
      matrix,
    });
    for (const observer of this._observer_set) {
      CommonNS.UtilNS.tryNoop(() => observer(view_box_vo));
    }
  }
  _calculateViewBoxVO({ x, y, width, height, matrix }) {
    const { min_x, min_y, max_x, max_y } = go(
      [
        [x, y],
        [x + width, y + height],
      ],
      mapL(CommonNS.UtilNS.createSVGPoint(this._svg_el)),
      mapL((point) => point.matrixTransform(matrix)),
      CommonNS.UtilNS.calculateMinMaxXY
    );
    return {
      x: min_x,
      y: min_y,
      width: max_x - min_x,
      height: max_y - min_y,
    };
  }
}
